import {nearestTranscriptWord,wordIdsBetween} from '../lib/transcriptSelection';
import {useEffect, useRef} from 'react';

// Mouse drag selection and touch selection share the same word-range model.
// Touch leaves vertical scrolling alone unless the user explicitly extends a range.
export default function useTranscriptSelection({body, selected, words, onSelect, onSelectRange, setFloating, mobileExtendMode=false}) {
  const drag=useRef(null), touchDrag=useRef(null), anchor=useRef(null), focus=useRef(null), frame=useRef(null), suppress=useRef(false);
  const latest=useRef(null);
  latest.current={selected,words,onSelect,onSelectRange,setFloating,mobileExtendMode};
  const nodes=()=>[...body.current.querySelectorAll('[data-word-id]')];
  const idsBetween=wordIdsBetween;
  const nearest=nearestTranscriptWord;
  const end=()=>{cancelAnimationFrame(frame.current);drag.current=null;touchDrag.current=null;};
  useEffect(()=>{
    window.addEventListener('blur',end);
    return()=>{end();window.removeEventListener('blur',end);};
  },[]);
  // With touch-action:pan-y the browser can still cancel a vertical gesture to
  // scroll normally. A horizontal drag selects words; in explicit range mode,
  // all directions select, including across wrapped lines.
  const updateTouchRange=(d,x,y)=>{
    const el=nearest(d.elements,x,y);
    const id=el?.dataset.wordId;
    if(!id || id===d.last)return;
    d.last=id;
    latest.current.onSelectRange?.(idsBetween(d.elements,d.anchor,id));
    latest.current.setFloating(null);
  };
  const startTouch=e=>{
    const el=e.target.closest?.('[data-word-id]');
    if(!el || !body.current?.contains(el))return;
    const elements=nodes(),id=String(el.dataset.wordId);
    const extending=latest.current.mobileExtendMode && latest.current.selected.length>0;
    // Hold the starting word steady while refining the range over several taps.
    const savedAnchor=String(anchor.current ?? latest.current.selected[0] ?? '');
    if(!extending)anchor.current=id;
    touchDrag.current={
      id:e.pointerId,elements,start:id,anchor:extending && elements.some(node=>node.dataset.wordId===savedAnchor)?savedAnchor:id,
      last:null,x:e.clientX,y:e.clientY,originX:e.clientX,originY:e.clientY,moved:false,extending
    };
    // Capture pointer moves across line breaks; pan-y still allows the
    // browser to cancel this gesture for a normal vertical scroll.
    try{body.current.setPointerCapture?.(e.pointerId);}catch{ /* Browser may own the pointer. */ }
  };
  const moveTouch=e=>{
    const d=touchDrag.current;
    if(!d || d.id!==e.pointerId)return false;
    d.x=e.clientX;d.y=e.clientY;
    // In the default mode a vertical gesture means scroll, not selection.
    if(!d.moved && !d.extending && Math.abs(d.y-d.originY)>Math.abs(d.x-d.originX)*1.15)return true;
    if(!d.moved && Math.hypot(d.x-d.originX,d.y-d.originY)>7)d.moved=true;
    if(d.moved) {
      suppress.current=true;
      updateTouchRange(d,d.x,d.y);
    }
    return true;
  };
  const finishTouch=e=>{
    const d=touchDrag.current;
    if(!d || d.id!==e.pointerId)return false;
    touchDrag.current=null;
    if(body.current?.hasPointerCapture?.(e.pointerId))body.current.releasePointerCapture(e.pointerId);
    suppress.current=true; // Ignore the synthetic click following pointerup.
    const endNode=nearest(d.elements,e.clientX,e.clientY);
    const endId=endNode?.dataset.wordId || d.start;
    if(d.moved || d.extending){
      latest.current.onSelectRange?.(idsBetween(d.elements,d.anchor,endId));
      latest.current.setFloating(null);
    }else{
      const word=latest.current.words.find(w=>String(w.id)===d.start);
      if(word){
        anchor.current=d.start;focus.current=d.start;
        latest.current.onSelect?.(word,false,false);
        latest.current.setFloating(null);
      }
    }
    return true;
  };
  const move=()=>{
    const d=drag.current;if(!d || !body.current)return;
    const rect=body.current.getBoundingClientRect();
    const speed=d.y<rect.top+28 ? -12 : d.y>rect.bottom-28 ? 12 : 0;
    if(speed && d.moved)body.current.scrollTop+=speed;
    const el=nearest(d.elements,d.x,Math.min(rect.bottom-2,Math.max(rect.top+2,d.y)));
    const id=el?.dataset.wordId;
    if(id && id!==d.last){
      d.last=id;focus.current=id;
      latest.current.onSelectRange?.(idsBetween(d.elements,d.anchor,id));
    }
    frame.current=requestAnimationFrame(move);
  };
  const onPointerDown=e=>{
    // The reading margin contains interactive comments. Clicking a bubble or
    // using its reply form must never initiate word selection or pointer capture.
    if(e.target.closest?.('button,input,textarea,select,[contenteditable],.transcript-margin-anchor,.transcript-margin-popover'))return;
    suppress.current=false;
    if(e.pointerType==='touch' || e.pointerType==='pen'){startTouch(e);return;}
    if(e.pointerType!=='mouse' || e.button!==0)return;
    const container=body.current;if(!container)return;
    // Keep the native scrollbar usable; whitespace inside the text is selectable.
    const bounds=container.getBoundingClientRect();
    if(e.clientX>=bounds.left+container.clientLeft+container.clientWidth || e.clientX<bounds.left+container.clientLeft)return;
    const elements=nodes();
    const el=e.target.closest('[data-word-id]') || nearest(elements,e.clientX,e.clientY);if(!el)return;
    e.preventDefault();window.getSelection()?.removeAllRanges();
    suppress.current=true;end();setFloating(null);body.current.focus({preventScroll:true});
    const id=el.dataset.wordId;
    const word=words.find(w=>String(w.id)===id);if(!word)return;
    if(e.shiftKey && anchor.current && elements.some(el=>el.dataset.wordId===anchor.current)){
      onSelectRange?.(idsBetween(elements,anchor.current,id));
    }else{
      anchor.current=id;onSelect(word,false,e.ctrlKey || e.metaKey);
    }
    focus.current=id;
    if(e.ctrlKey || e.metaKey)return;
    drag.current={elements,anchor:anchor.current,last:id,x:e.clientX,y:e.clientY,startX:e.clientX,startY:e.clientY,moved:false,pointerId:e.pointerId};
    body.current.setPointerCapture(e.pointerId);
    frame.current=requestAnimationFrame(move);
  };
  const onPointerMove=e=>{
    if(moveTouch(e))return;
    const d=drag.current;if(!d || d.pointerId!==e.pointerId)return;
    d.x=e.clientX;d.y=e.clientY;
    if(Math.hypot(d.x-d.startX,d.y-d.startY)>3)d.moved=true;
  };
  const onPointerUp=e=>{
    if(finishTouch(e))return;
    const d=drag.current;if(!d || d.pointerId!==e.pointerId)return;
    // Include the release position even if the last animation frame has not run.
    d.x=e.clientX;d.y=e.clientY;cancelAnimationFrame(frame.current);move();
    const el=d.elements.find(el=>el.dataset.wordId===d.last);end();
    if(body.current.hasPointerCapture(e.pointerId))body.current.releasePointerCapture(e.pointerId);
    const r=el?.getBoundingClientRect();
    if(r)setFloating({left:r.left,right:r.right,top:r.top,bottom:r.bottom});
  };
  const onKeyDown=e=>{
    const elements=nodes();if(!elements.length)return;
    if((e.ctrlKey || e.metaKey) && e.key.toLowerCase()==='a'){
      e.preventDefault();e.stopPropagation();anchor.current=elements[0].dataset.wordId;focus.current=elements.at(-1).dataset.wordId;
      onSelectRange?.(elements.map(el=>el.dataset.wordId));setFloating(null);return;
    }
    if(e.key==='Escape'){e.preventDefault();onSelectRange?.([]);setFloating(null);return;}
    if(!['ArrowLeft','ArrowRight','ArrowUp','ArrowDown','Home','End'].includes(e.key))return;
    e.preventDefault();e.stopPropagation();window.getSelection()?.removeAllRanges();
    const focused=e.target.closest('[data-word-id]')?.dataset.wordId || focus.current || String(selected.at(-1));
    let i=elements.findIndex(el=>el.dataset.wordId===focused);if(i<0)i=0;
    let target=i;
    if(e.key==='Home')target=0;
    else if(e.key==='End')target=elements.length-1;
    else if(e.key==='ArrowLeft')target=Math.max(0,i-1);
    else if(e.key==='ArrowRight')target=Math.min(elements.length-1,i+1);
    else {
      const r=elements[i].getBoundingClientRect(),direction=e.key==='ArrowDown'?1:-1;
      const candidates=elements.filter(el=>direction*(el.getBoundingClientRect().top-r.top)>2);
      const el=nearest(candidates,r.left,direction>0?r.bottom+5:r.top-5);if(el)target=elements.indexOf(el);
    }
    const id=elements[target].dataset.wordId;
    if(e.shiftKey){
      if(!anchor.current || !elements.some(el=>el.dataset.wordId===anchor.current))anchor.current=elements[i].dataset.wordId;
      onSelectRange?.(idsBetween(elements,anchor.current,id));
    }else{anchor.current=id;onSelectRange?.([id]);}
    focus.current=id;elements[target].focus({preventScroll:true});elements[target].scrollIntoView({block:'nearest'});setFloating(null);
  };
  const onCopy=e=>{
    if(!selected.length)return;
    const ids=new Set(selected);e.clipboardData.setData('text/plain',words.filter(w=>ids.has(w.id)).map(w=>w.text).join(' '));e.preventDefault();
  };
  return {onPointerDown,onPointerMove,onPointerUp,onPointerCancel:end,onLostPointerCapture:end,onKeyDown,onCopy,
    suppressClick:()=>{if(suppress.current){suppress.current=false;return true;}return false;}};
}
